import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { saveFileResponse } from '@app/helpers/file';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { MRT_SortingState } from 'material-react-table';
import { parseAsInteger, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { buildFilterParsers } from '../helpers/build-filter-parsers';
import { parseAsMrtSort } from '../helpers/parse-as-mrt-sort';
import { FilterDefinition, ListViewExportParams, ListViewSort } from '../types';
import { useStickyListViewState } from './useStickyListViewState';

export interface UseListViewLogicProps {
  listId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters?: FilterDefinition<any, any>[];
  defaultPageSize?: number;
  exportFn?: (params: ListViewExportParams) => Promise<AxiosResponse<unknown, unknown>>;
}

/**
 * Hook that encapsulates the shared logic for ListView components.
 * Handles URL state management, filter parsing, sticky state, and export/refresh actions.
 */
export function useListViewLogic({
  listId,
  filters,
  defaultPageSize = 50,
  exportFn,
}: UseListViewLogicProps) {
  const queryClient = useQueryClient();
  const capture = usePostHogCapture({ domain: 'listview' });

  // Parsers for filter keys only
  const parsers = useMemo(() => (filters ? buildFilterParsers(filters) : {}), [filters]);

  // Merge ALL parsers into one
  const allParsers = useMemo(
    () => ({
      ...parsers,
      pageIndex: parseAsInteger.withDefault(0),
      pageSize: parseAsInteger.withDefault(defaultPageSize),
      sort: parseAsMrtSort,
    }),
    [parsers, defaultPageSize],
  );

  // Use ONE useQueryStates for everything
  const [allParams, setAllParams] = useQueryStates(allParsers, {
    history: 'replace',
  });

  // Manage sticky state
  const { clearStoredState } = useStickyListViewState(
    listId,
    allParams,
    setAllParams,
    defaultPageSize,
    filters,
  );

  // Smart setters
  const setFilterValue = useCallback(
    (key: string, value: unknown) => {
      setAllParams((old) => {
        const nextParams = { ...old, [key]: value, pageIndex: 0 };
        const { pageIndex: _pi, pageSize: _ps, sort: _s, ...filterState } = nextParams;

        capture('filter_changed', {
          list_id: listId,
          filter_key: key,
          filter_value: value,
          filters: filterState,
        });

        return nextParams;
      });
    },
    [setAllParams, capture, listId],
  );

  // Reset filters
  const resetFilters = useCallback(() => {
    setAllParams((old) => ({
      ...old,
      pageIndex: 0,
      ...Object.fromEntries((filters ?? []).map((f) => [f.name, null])),
    }));
    clearStoredState();
    capture('filters_reset', { list_id: listId });
  }, [filters, setAllParams, clearStoredState, capture, listId]);

  const onExport = async (format: 'csv' | 'xls'): Promise<void> => {
    if (!exportFn) {
      return;
    }

    const { pageIndex, pageSize, sort, ...urlFilters } = allParams;

    const result = await exportFn({
      sort: sort ?? undefined,
      filters: urlFilters,
      format,
    });

    saveFileResponse(result);
  };

  const onRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: [listId, allParams],
    });
  };

  const sorting: MRT_SortingState = allParams.sort ? [allParams.sort] : [];

  const handleSortingChange = useCallback(
    (updaterOrValue: MRT_SortingState | ((old: MRT_SortingState) => MRT_SortingState)) => {
      const newSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      const next: ListViewSort | null = newSorting[0] ?? null;
      setAllParams((old) => ({ ...old, sort: next, pageIndex: 0 }));
      capture('sort_changed', { list_id: listId, sort: next });
    },
    [sorting, setAllParams, capture, listId],
  );

  const contextValue = useMemo(
    () => ({
      listId,
      filters: filters ?? [],
      parsers,
      setFilterValue,
      resetFilters,
    }),
    [listId, filters, parsers, setFilterValue, resetFilters],
  );

  return {
    allParams,
    setAllParams,
    contextValue,
    onExport: exportFn ? onExport : undefined,
    onRefresh,
    handleSortingChange,
    sorting,
  };
}
